// Credential resolution for the analysis WebSocket.
//
// The backend gates /ws/analysis on a ?token= that must be either the shared
// INTERNAL_API_TOKEN or a valid JWT. The dashboard authenticates with the
// logged-in user's JWT (mirroring restClient's `getAuthToken() || WS_TOKEN`),
// falling back to the optional build-time WS_TOKEN for headless/machine clients.
//
// Kept separate from config.ts to avoid a circular import: auth/tokenStorage and
// auth/refresh both import from config, so config must not import them back.
import { WS_TOKEN } from 'config';
import { getAuthToken, getRefreshToken } from 'auth/tokenStorage';
import { refreshAccessToken } from 'auth/refresh';
import { isAccessTokenExpired } from 'auth/jwt';

/**
 * Resolve the token to present on the next WS handshake, proactively refreshing
 * a near-expiry access token first. Unlike the optional-auth REST analyze
 * endpoints, the WS hard-rejects an expired JWT (close code 1008), so refreshing
 * up front avoids a guaranteed reconnect cycle. `refreshAccessToken` is
 * single-flight and persists the new token via `setTokens`, so a concurrent REST
 * refresh is deduped. A failed/transient refresh falls through to the (stale)
 * token so the attempt still proceeds and the backoff loop retries.
 */
export async function freshWsToken(): Promise<string> {
  if (isAccessTokenExpired(getAuthToken()) && getRefreshToken()) {
    await refreshAccessToken();
  }
  return getAuthToken() || WS_TOKEN;
}
